import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { Calculator } from 'lucide-react';
import { Scaffold } from 'layouts';
import { PageHeaderForFilters } from 'components/ui/page-header-for-filters';
import { Button } from 'components/ui/button';
import { DataTableV2, type SortingProps } from 'components/ui/DataTableV2';
import { notify } from 'services/notifications';
import { usePermission } from 'hooks/usePermission';
import { useViagensControleSearchParams } from 'hooks/store/viagens-controle';
import { cn } from 'lib/utils';
import {
  useApproveViagemControle,
  useFetchViagensControle,
  useRejectViagemControle,
} from 'hooks/api/viagens-controle';
import { useViagensControleColumns } from './hooks';
import {
  ViagensControleFilter,
  type ViagensControleFilterRef,
} from './components/viagens-controle-filter';
import { ViagensControleIndicators } from './components/viagens-controle-indicators';
import { PageTitle } from './components/page-title';
import { TabbedNavigation } from './components/tabbed-navigation';
import { ViagemControleActivitiesDialog } from './viagem-controle-activities-dialog';
import { ViagemControleRejectDialog } from './viagem-controle-reject-dialog';
import { exportViagensControle } from './viagens-controle-export';
import type { ViagemControle } from './types';

const QUERY_KEY = 'fetch-paginated-viagens-controle';

type Props = {
  approvalMode?: boolean;
};

export const ViagensControleListPage = ({ approvalMode = false }: Props) => {
  const queryClient = useQueryClient();
  const filtersRef = React.useRef<ViagensControleFilterRef>(null);
  const [activitiesViagem, setActivitiesViagem] =
    React.useState<ViagemControle | null>(null);
  const [rejectViagem, setRejectViagem] =
    React.useState<ViagemControle | null>(null);
  const [actionLoadingId, setActionLoadingId] = React.useState<number | null>(
    null
  );
  const [filterLoadingAction, setFilterLoadingAction] = React.useState<
    'filter' | 'reset' | null
  >(null);
  const [reportExportLoading, setReportExportLoading] = React.useState(false);
  const { hasPermissionTo } = usePermission();
  const { fetchViagensControle } = useFetchViagensControle();
  const { approveViagemControle } = useApproveViagemControle();
  const { rejectViagemControle } = useRejectViagemControle();
  const {
    filters,
    paginationControl,
    updateFilters,
    updatePaginationControl,
    clearState,
  } = useViagensControleSearchParams({ persisted: true });
  const queryFilters = React.useMemo(() => {
    if (!approvalMode) return filters;

    return {
      ...filters,
      approvalStatus: 'validation' as const,
    };
  }, [approvalMode, filters]);

  const invalidateList = React.useCallback(() => {
    queryClient.invalidateQueries(QUERY_KEY);
    queryClient.invalidateQueries('fetch-viagens-controle-indicators');
  }, [queryClient]);

  const approveMutation = useMutation({
    mutationKey: ['approve-viagem-controle'],
    mutationFn: approveViagemControle,
    onSuccess: () => {
      notify('success', 'Viagem validada com sucesso.');
      invalidateList();
    },
    onError: (error: any) => notify('error', error?.message),
  });

  const rejectMutation = useMutation({
    mutationKey: ['reject-viagem-controle'],
    mutationFn: rejectViagemControle,
    onSuccess: () => {
      notify('success', 'Viagem invalidada com sucesso.');
      setRejectViagem(null);
      invalidateList();
    },
    onError: (error: any) => notify('error', error?.message),
  });

  const viagensQuery = useQuery({
    queryKey: [QUERY_KEY, approvalMode, queryFilters, paginationControl],
    queryFn: () => {
      return fetchViagensControle({
        filters: queryFilters,
        pagination: paginationControl,
        approveOrRejectOnly: approvalMode,
      });
    },
    refetchOnWindowFocus: false,
  });

  const handleFilters = React.useCallback(
    (f: typeof filters) => {
      setFilterLoadingAction('filter');
      updateFilters(f);
      updatePaginationControl({ page: 1 });
    },
    [updateFilters, updatePaginationControl]
  );

  const handleResetFilters = React.useCallback(() => {
    setFilterLoadingAction('reset');
    clearState();
  }, [clearState]);

  const handleSort = React.useCallback(
    (p: SortingProps) => {
      updatePaginationControl({
        orderBy: p.orderBy,
        direction: p.orderByDirection,
        page: 1,
      });
    },
    [updatePaginationControl]
  );

  const handleApprove = React.useCallback(
    (id: number) => {
      setActionLoadingId(id);
      approveMutation.mutate(id, {
        onSettled: () => setActionLoadingId(null),
      });
    },
    [approveMutation]
  );

  const handleReject = React.useCallback(
    (payload: { id: number; justificationId: number; comment: string }) => {
      setActionLoadingId(payload.id);
      rejectMutation.mutate(payload, {
        onSettled: () => setActionLoadingId(null),
      });
    },
    [rejectMutation]
  );

  const handleExport = React.useCallback(async () => {
    const total = viagensQuery.data?.meta?.total ?? 0;

    if (!total) {
      notify('error', 'Não existem viagens para exportar.');
      return;
    }

    try {
      setReportExportLoading(true);
      const viagens = await fetchViagensControle({
        filters: queryFilters,
        pagination: {
          page: 1,
          limit: total,
        },
        approveOrRejectOnly: approvalMode,
      });

      exportViagensControle(viagens.data);
      notify('success', 'Relatório de viagens gerado com sucesso.');
    } catch (error: any) {
      notify('error', error?.message || 'Não foi possível exportar viagens.');
    } finally {
      setReportExportLoading(false);
    }
  }, [approvalMode, fetchViagensControle, queryFilters, viagensQuery.data?.meta?.total]);

  const columns = useViagensControleColumns({
    actionLoadingId: approvalMode ? actionLoadingId : null,
    canApprove: approvalMode && hasPermissionTo(['APPROVE_VIAGENS_CONTROLE']),
    canReject: approvalMode && hasPermissionTo(['REJECT_VIAGENS_CONTROLE']),
    onApprove: approvalMode ? handleApprove : undefined,
    onReject: approvalMode ? setRejectViagem : undefined,
    onShowActivities: approvalMode ? undefined : setActivitiesViagem,
    showActions: true,
    showApprovalStatus: true,
  });

  React.useEffect(() => {
    if (filterLoadingAction && !viagensQuery.isFetching) {
      setFilterLoadingAction(null);
    }
  }, [filterLoadingAction, viagensQuery.isFetching]);

  return (
    <Scaffold>
      <ViagemControleActivitiesDialog
        viagem={activitiesViagem}
        onOpenChange={(open) => !open && setActivitiesViagem(null)}
      />
      <ViagemControleRejectDialog
        viagem={rejectViagem}
        loading={rejectMutation.isLoading}
        onOpenChange={(open) => !open && setRejectViagem(null)}
        onSubmit={({ justificationId, comment }) => {
          if (!rejectViagem) return;
          handleReject({ id: rejectViagem.id, justificationId, comment });
        }}
      />
      <TabbedNavigation />
      <div className="flex flex-col gap-6">
        <PageHeaderForFilters
          headerTitle={
            <PageTitle
              subtitle={approvalMode ? 'Validação Operacional' : 'Controle'}
            />
          }
        >
          <ViagensControleFilter
            ref={filtersRef}
            onSubmitFilters={handleFilters}
            onResetFilters={handleResetFilters}
            filterLoadingAction={filterLoadingAction}
            showApprovalStatusFilter={!approvalMode}
          />
        </PageHeaderForFilters>

        {!approvalMode && (
          <ViagensControleIndicators
            filters={filters}
            onSubmitFilters={handleFilters}
          />
        )}

        <DataTableV2
          data={viagensQuery?.data?.data ?? []}
          columns={columns}
          initialColumnVisibility={{ idViagem: false }}
          manualPagination
          showControlColumns
          inlineTopBarControls
          noBreakHeader
          onPaginationChange={updatePaginationControl}
          onSort={handleSort}
          isLoading={viagensQuery.isLoading}
          stickyHeader={true}
          rightPinnedColumns={['acoes']}
          renderTopBar={() => (
            <div className="ml-auto inline-flex h-fit w-fit shrink-0 rounded-md bg-white p-0">
              <Button
                type="button"
                size="sm"
                className="h-9 bg-emerald-600 px-5 font-GilroySemibold text-white hover:bg-emerald-700"
                disabled={reportExportLoading || !viagensQuery.data?.meta?.total}
                onClick={handleExport}
              >
                <Calculator className="mr-2 h-4 w-4" />
                Exportar
              </Button>
            </div>
          )}
          tableWrapperClassName={cn(
            approvalMode
              ? 'max-h-[calc(100vh-280px)]'
              : 'max-h-[calc(100vh-430px)]'
          )}
          pagination={{
            ...viagensQuery?.data?.meta,
            currentPage: paginationControl.page,
            perPage: paginationControl.limit,
          }}
        />
      </div>
    </Scaffold>
  );
};
